import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCandidatos, fmtVotos, fmtPct, fmtFecha } from "@/lib/candidatos";
import { cn } from "@/lib/cn";

type SortKey = "ranking" | "votos" | "pct_validos";

export function CandidatosPage() {
  const [sort, setSort] = useState<SortKey>("ranking");
  const [showWhite, setShowWhite] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ["candidatos", sort],
    queryFn: () => fetchCandidatos({ sort }),
  });

  if (isLoading) {
    return (
      <div className="container-prose py-16">
        <span className="chip-vacio">Cargando…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="container-prose py-16">
        <span className="chip-contradiccion">Error al cargar candidatos</span>
      </div>
    );
  }

  const { proceso, candidatos } = data;
  const visibles = showWhite ? candidatos : candidatos.filter((c) => c.tipo === "candidato");

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 PRESIDENCIAL · Distrito Único</span>
        <span className="chip-probado">🟢 Fuente oficial ONPE + JNE</span>
        {proceso && (
          <span className="chip-vacio" title={proceso.fuente}>
            snapshot {fmtFecha(proceso.fetchedAt)}
          </span>
        )}
      </div>

      <h1 className="mt-6 font-serif text-4xl text-ink-50 md:text-5xl">
        Auditoría: <span className="text-gold-300">¿Qué pasó con cada candidato?</span>
      </h1>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        Universo completo de la elección presidencial EG2026:{" "}
        <strong className="text-ink-50">36 candidatos</strong> que se presentaron + voto en blanco +
        voto nulo. <strong>Sin TOPs, sin podios, sin truncar.</strong> Cada fila lleva a la ficha
        completa del candidato con su plancha y expediente JNE.
      </p>

      {proceso && (
        <section className="mt-8 grid gap-3 md:grid-cols-4">
          <ResumenCard
            label="Electores hábiles"
            value={proceso.electoresHabiles ? fmtVotos(proceso.electoresHabiles) : "—"}
          />
          <ResumenCard
            label="Votos emitidos"
            value={fmtVotos(proceso.totalEmitidos)}
            hint={`${fmtPct(proceso.participacion, 2)} participación`}
          />
          <ResumenCard
            label="Votos válidos"
            value={fmtVotos(proceso.totalValidos)}
            hint={`Blancos ${fmtVotos(proceso.votosBlancos)} · Nulos ${fmtVotos(proceso.votosNulos)}`}
          />
          <ResumenCard
            label="Actas contabilizadas"
            value={fmtPct(proceso.pctContabilizadas, 2)}
            hint={`${fmtVotos(proceso.contabilizadas)} / ${fmtVotos(proceso.totalActas)} actas`}
          />
        </section>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-ink-700/40 pt-6">
        <span className="text-sm text-ink-300">Ordenar:</span>
        {(
          [
            ["ranking", "Ranking oficial"],
            ["votos", "Votos"],
            ["pct_validos", "% válidos"],
          ] as Array<[SortKey, string]>
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setSort(k)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition",
              sort === k
                ? "bg-gold-500/15 text-gold-200"
                : "text-ink-300 hover:bg-ink-800 hover:text-ink-50"
            )}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto" />
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input
            type="checkbox"
            checked={showWhite}
            onChange={(e) => setShowWhite(e.target.checked)}
            className="accent-gold-500"
          />
          Incluir Voto en Blanco / Nulo
        </label>
      </div>

      {/* Tabla universo completo con fotos */}
      <div className="mt-4 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
              <th className="py-3 px-3 text-center w-12">#</th>
              <th className="px-2 w-14"></th>
              <th className="px-3">Candidato</th>
              <th className="px-3 w-10"></th>
              <th className="px-3">Organización</th>
              <th className="px-3 text-right">Votos</th>
              <th className="px-3 text-right" title="Sobre votos válidos">% val.</th>
              <th className="px-3 text-right" title="Sobre votos emitidos">% emit.</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((c) => {
              const isVacio = c.tipo !== "candidato";
              return (
                <tr
                  key={c.id}
                  className={cn(
                    "border-b border-ink-700/30 transition hover:bg-ink-800/50",
                    isVacio && "bg-ink-800/30"
                  )}
                >
                  <td className="py-2 px-3 text-ink-300 font-mono text-center">#{c.ranking}</td>
                  <td className="px-2">
                    {c.fotoUrl ? (
                      <Link to={`/candidatos/${c.slug}`}>
                        <img
                          src={c.fotoUrl}
                          alt={c.nombreCandidato}
                          className="h-10 w-10 rounded-full object-cover ring-1 ring-ink-700/60 hover:ring-gold-500/60 transition"
                          loading="lazy"
                        />
                      </Link>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-ink-800 grid place-items-center text-ink-400 text-xs">
                        {isVacio ? "—" : "?"}
                      </div>
                    )}
                  </td>
                  <td className="px-3">
                    <Link to={`/candidatos/${c.slug}`} className="block hover:text-gold-200">
                      <div className="font-medium text-ink-50">
                        {c.iniciales && (
                          <span className="mr-2 inline-block rounded bg-gold-500/15 px-1.5 py-0.5 text-xs text-gold-300 font-mono">
                            {c.iniciales}
                          </span>
                        )}
                        {c.nombreCandidato}
                      </div>
                      {isVacio && (
                        <div className="text-xs text-ink-400 mt-0.5">(no es candidatura)</div>
                      )}
                    </Link>
                  </td>
                  <td className="px-1">
                    {c.logoUrl && (
                      <img
                        src={c.logoUrl}
                        alt={c.nombreOrganizacion}
                        className="h-7 w-7 rounded object-contain"
                        loading="lazy"
                      />
                    )}
                  </td>
                  <td className="px-3 text-ink-300 text-xs">{c.nombreOrganizacion}</td>
                  <td className="px-3 text-right text-ink-100 font-mono tabular-nums">
                    {fmtVotos(c.votosAbsolutos)}
                  </td>
                  <td className="px-3 text-right font-mono tabular-nums">
                    {isVacio ? (
                      <span className="text-ink-400">—</span>
                    ) : (
                      <span className="text-gold-300">{fmtPct(c.pctValidos)}</span>
                    )}
                  </td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-200">
                    {fmtPct(c.pctEmitidos)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink-700 bg-ink-950/40 text-ink-200">
              <td className="py-3 px-3 font-semibold" colSpan={5}>
                Total {visibles.length} filas
              </td>
              <td className="px-3 text-right font-mono tabular-nums">
                {fmtVotos(visibles.reduce((s, c) => s + c.votosAbsolutos, 0))}
              </td>
              <td className="px-3 text-right" colSpan={2}>
                {proceso && (
                  <span className="text-xs text-ink-400">
                    Emitidos: {fmtVotos(proceso.totalEmitidos)} · Válidos:{" "}
                    {fmtVotos(proceso.totalValidos)}
                  </span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-6 rounded-lg border border-ink-700/40 bg-ink-900/40 p-4 text-xs text-ink-300">
        <strong className="text-ink-100">Fuentes oficiales:</strong> Votos y % de{" "}
        <code className="text-ink-200">{proceso?.fuente}</code>. Fotos, organizaciones y plancha
        (Presidente + 1° y 2° Vicepresidente) del portal{" "}
        <a
          href="https://votoinformado.jne.gob.pe/elecciones-generales"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gold-300 hover:underline"
        >
          Voto Informado JNE
        </a>{" "}
        (idProcesoElectoral 124 · tipo presidencial). Hoja de vida completa (bienes, formación,
        experiencia, antecedentes, propuestas) llegará en bloque F (sync con browser real).
      </div>
    </div>
  );
}

function ResumenCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-ink-700/60 bg-ink-900/60 p-4">
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className="mt-1 font-mono text-2xl tabular-nums text-ink-50">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}
